import styled, { css } from 'styled-components';
import { flexColumn, flexCenterVertical } from '@stylesheets/helpers';

export const Wrapper = styled.div`
  ${flexColumn};
  max-width: 1100px;
  width: 100%;

  ${({ theme: { spacing } }) => css`
    padding: ${spacing.level6} ${spacing.level6} 0;
    gap: ${spacing.level2};
  `}

  @media (max-width: 768px) {
    ${({ theme: { spacing } }) => css`
      padding: ${spacing.level5} ${spacing.level4} 0;
    `}
  }
`;

export const CurrentUserInfo = styled.div`
  ${flexCenterVertical};

  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.bodySmall.fontSize};
    line-height: ${font.bodySmall.lineHeight};
    color: ${colors.onSurfaceVariant};
    padding: 0 ${spacing.level4};
    gap: ${spacing.level2};
  `}
`;

export const OrgNameRow = styled.div`
  ${flexCenterVertical};
  justify-content: space-between;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level4};
    padding: ${spacing.level3} ${spacing.level4};
  `}
`;

export const OrgNameLabel = styled.span`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurface};
  `}
`;

export const OrgNameValue = styled.div`
  ${flexCenterVertical};
  min-width: 0;
  overflow-wrap: anywhere;

  ${({ theme: { spacing, font, colors } }) => css`
    gap: ${spacing.level2};
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurfaceVariant};
  `}
`;

export const EditRow = styled.div`
  ${flexCenterVertical};

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level3};
    padding: ${spacing.level3} ${spacing.level4};
  `}

  @media (max-width: 600px) {
    flex-wrap: wrap;
  }
`;

export const TypeRow = styled.div`
  ${flexCenterVertical};
  justify-content: space-between;

  ${({ theme: { spacing } }) => css`
    padding: ${spacing.level3} ${spacing.level4};
  `}
`;

export const TypeLabel = styled.span`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurface};
  `}
`;

export const TypeBadge = styled.span<{ $variant?: 'personal' | 'team' }>`
  ${flexCenterVertical};

  ${({ theme: { spacing, borderRadius, font, colors }, $variant }) => css`
    gap: ${spacing.level2};
    padding: ${spacing.level1} ${spacing.level3};
    border-radius: ${borderRadius.full};
    font-size: ${font.labelSmall.fontSize};
    font-weight: ${font.labelSmall.fontWeight};
    letter-spacing: 0.04em;
    background: ${$variant === 'personal' ? colors.surfaceContainerHigh : colors.primaryContainer};
    color: ${$variant === 'personal' ? colors.onSurface : colors.onPrimaryContainer};

    & > svg {
      width: 14px;
      height: 14px;
    }
  `}
`;

export const Input = styled.input`
  flex: 1;

  ${({ theme: { colors, font, spacing, borderRadius } }) => css`
    padding: ${spacing.level3} ${spacing.level4};
    border-radius: ${borderRadius.small};
    border: 1px solid ${colors.outline};
    background: transparent;
    color: ${colors.onSurface};
    font-size: ${font.bodyMedium.fontSize};
    font-family: ${font.bodyMedium.fontFamily};
    min-width: 200px;

    &:focus {
      outline: none;
      border-color: ${colors.primary};
      box-shadow: 0 0 0 1px ${colors.primary};
    }

    &::placeholder {
      color: ${colors.onSurfaceVariant};
    }

    @media (max-width: 600px) {
      width: 100%;
      min-width: 0;
    }
  `}
`;

export const DateInput = styled.input`
  ${({ theme: { colors, font, spacing, borderRadius } }) => css`
    padding: ${spacing.level3} ${spacing.level4};
    border-radius: ${borderRadius.small};
    border: 1px solid ${colors.outline};
    background: transparent;
    color: ${colors.onSurface};
    font-size: ${font.bodySmall.fontSize};
    font-family: ${font.bodySmall.fontFamily};

    &:focus {
      outline: none;
      border-color: ${colors.primary};
      box-shadow: 0 0 0 1px ${colors.primary};
    }
  `}
`;

export const ButtonRow = styled.div`
  display: flex;
  align-items: center;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level3};
    padding: ${spacing.level3} 0;
  `}
`;

export const FrameButtonRow = styled.div`
  display: flex;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level3};
    padding: ${spacing.level3} ${spacing.level4};
  `}
`;

export const InviteForm = styled.form`
  display: flex;
  align-items: flex-end;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level3};
    padding: ${spacing.level2} ${spacing.level4};
  `}

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const InputGroup = styled.div`
  ${flexColumn};
  flex: 1;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level2};
  `}
`;

export const CompactInputGroup = styled.div`
  ${flexColumn};

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level2};
  `}
`;

export const SmallLabel = styled.label`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.labelSmall.fontSize};
    font-weight: ${font.labelSmall.fontWeight};
    color: ${colors.onSurfaceVariant};
    text-transform: uppercase;
    letter-spacing: 0.06em;
  `}
`;

export const InviteRow = styled.div`
  ${flexCenterVertical};
  justify-content: space-between;

  ${({ theme: { spacing, colors } }) => css`
    padding: ${spacing.level3} ${spacing.level4};
    border-bottom: 1px solid ${colors.surfaceContainerHighest};

    &:last-child {
      border-bottom: none;
    }
  `}

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;

    ${({ theme: { spacing } }) => css`
      gap: ${spacing.level3};
    `}
  }
`;

export const InviteInfo = styled.div`
  ${flexColumn};

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level1};
  `}
`;

export const InviteEmail = styled.span`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    font-weight: ${font.labelLarge.fontWeight};
    color: ${colors.onSurface};
  `}
`;

export const InviteMeta = styled.span`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodySmall.fontSize};
    line-height: ${font.bodySmall.lineHeight};
    color: ${colors.onSurfaceVariant};
  `}
`;

export const MemberTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.bodyMedium.fontSize};

    th,
    td {
      padding: ${spacing.level2} ${spacing.level3};
      text-align: left;
      white-space: nowrap;
    }

    th {
      font-weight: ${font.labelMedium.fontWeight};
      color: ${colors.onSurfaceVariant};
      font-size: ${font.labelSmall.fontSize};
      text-transform: uppercase;
      letter-spacing: 0.06em;
      border-bottom: 1px solid ${colors.outlineVariant};
    }

    td {
      color: ${colors.onSurface};
      border-bottom: 1px solid ${colors.surfaceContainerHighest};
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    tbody tr:hover {
      background: ${colors.surfaceContainerLow};
    }

    @media (max-width: 768px) {
      th,
      td {
        padding: ${spacing.level2};
      }
    }
  `}
`;

export const ActionsCell = styled.div`
  ${flexCenterVertical};

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level2};
  `}
`;

export const SmallIconButton = styled.span`
  display: inline-flex;

  & button {
    padding: 4px;
  }

  & svg {
    width: 16px;
    height: 16px;
  }
`;

export const RoleBadge = styled.span<{ $variant?: string }>`
  display: inline-block;

  ${({ theme: { spacing, borderRadius, font, colors }, $variant }) => css`
    padding: ${spacing.level1} ${spacing.level3};
    border-radius: ${borderRadius.full};
    font-size: ${font.labelSmall.fontSize};
    font-weight: ${font.labelSmall.fontWeight};
    letter-spacing: 0.04em;
    background: ${$variant === 'admin' ? colors.primaryContainer : colors.surfaceContainerHigh};
    color: ${$variant === 'admin' ? colors.onPrimaryContainer : colors.onSurface};
  `}
`;

export const HintText = styled.span`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodySmall.fontSize};
    line-height: ${font.bodySmall.lineHeight};
    color: ${colors.onSurfaceVariant};
    font-style: italic;
  `}
`;

export const CreateOrgForm = styled.form`
  display: flex;
  align-items: flex-end;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level3};
    padding: ${spacing.level2} ${spacing.level4};
  `}

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SectionDescription = styled.p`
  margin: 0;

  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    line-height: ${font.bodyMedium.lineHeight};
    color: ${colors.onSurfaceVariant};
    padding: ${spacing.level2} ${spacing.level4};
  `}
`;

export const RoleDropdownCell = styled.div`
  min-width: 120px;
`;

export const NameCell = styled.div`
  ${flexColumn};

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level1};
  `}
`;

export const InlineChips = styled.div`
  display: flex;
  flex-wrap: wrap;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level1};
  `}
`;

export const CompactRemoveButton = styled.span`
  ${({ theme: { font } }) => css`
    & button {
      padding: 6px 12px;
    }
    & button span {
      font-size: ${font.labelSmall.fontSize};
    }
  `}
`;

export const InviteButtonWrapper = styled.span`
  ${({ theme: { font } }) => css`
    & button {
      padding: 8px 16px;
    }
    & button span {
      font-size: ${font.labelMedium.fontSize};
    }
  `}
`;

export const DangerButtonWrapper = styled.span`
  ${({ theme: { colors } }) => css`
    &&&& button > span {
      background-color: ${colors.error};
    }
    &&&& button > span > span:last-child {
      color: ${colors.onError};
    }
    &&&& button:hover > span {
      opacity: 0.9;
    }
  `}
`;
